import { useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Sparkles, Mail, Check } from "lucide-react";

/**
 * Shows the first time a Google-authenticated user lands after login
 * if they've never been asked for marketing consent.
 * Persists choice to /api/me/marketing-consent which also stamps consent_asked_at.
 */
export default function MarketingConsentModal() {
  const { user, checkAuth } = useAuth();
  const [checked, setChecked] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Only show if user exists, is admin=false OR true doesn't matter, and consent was never asked.
  if (!user || user.consent_asked_at || dismissed) return null;

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/me/marketing-consent", { consent: checked });
      await checkAuth();
    } finally {
      setSaving(false);
      setDismissed(true);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
      <div
        className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-6 sm:p-7 ss-fadein"
        data-testid="marketing-consent-modal"
      >
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500">
          <Sparkles className="w-3.5 h-3.5 text-yellow-500" /> Witaj w SocialStart!
        </div>
        <h2 className="mt-2 text-2xl font-extrabold tracking-tight text-slate-900 leading-tight">
          Chcemy być pomocni — a nie natrętni.
        </h2>
        <p className="mt-3 text-sm text-slate-600 leading-relaxed">
          Od czasu do czasu chętnie wyślemy Ci porady jak lepiej się uczyć,
          nowości w aplikacji i okazjonalne oferty rabatowe. Bez spamu.
          Możesz wypisać się jednym kliknięciem w każdej chwili.
        </p>

        <label className="mt-5 flex items-start gap-3 cursor-pointer select-none bg-slate-50 border border-slate-200 rounded-2xl p-4 hover:border-slate-900 transition-colors">
          <input
            type="checkbox"
            data-testid="marketing-consent-checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="mt-0.5 w-5 h-5 accent-[#FFD600] flex-shrink-0"
          />
          <span className="text-sm text-slate-900">
            <b>Chcę dostawać oferty i porady o nauce</b>
            <span className="block text-xs text-slate-500 mt-0.5">
              Wyrażam zgodę na otrzymywanie od SocialStart informacji marketingowych na adres {user.email}. Zgodę mogę wycofać w każdej chwili.
            </span>
          </span>
        </label>

        <button
          data-testid="marketing-consent-save"
          onClick={save}
          disabled={saving}
          className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#FFD600] hover:bg-[#E6C200] disabled:bg-yellow-200 text-slate-900 font-bold text-base px-6 py-3.5 rounded-2xl btn-yellow-glow"
        >
          {saving ? "Zapisuję..." : (
            <>
              <Check className="w-4 h-4" strokeWidth={2.5} />
              {checked ? "Zapisz się i kontynuuj" : "Zapisz wybór i kontynuuj"}
            </>
          )}
        </button>
        <div className="mt-3 text-center text-xs text-slate-400 flex items-center justify-center gap-1.5">
          <Mail className="w-3 h-3" /> Wypisujesz się jednym kliknięciem w mailu.
        </div>
      </div>
    </div>
  );
}
